import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "../../components/layout/SiteShell";
import ContentPageLayout from "../../components/content/ContentPageLayout";
import SEO from "../../components/seo/SEO";
import ConversionCtas from "../../components/content/ConversionCtas";
import DarkCard from "../../components/layout/DarkCard";
import { HOME_CTA_DARK_OUTLINE, HOME_CTA_DARK_PRIMARY } from "../../lib/homeCta";

const SECTIONS = [
  {
    id: "why-people",
    title: "Why people add sea moss to routines",
    body: [
      "Many people look up sea moss when they want a simple fridge-ready food they can stir into everyday drinks and bowls.",
      "Interest often comes from coastal food traditions, mineral talk online, and curiosity about keeping a small daily habit — not from medical advice.",
    ],
  },
  {
    id: "how-people-use",
    title: "How people use it",
    body: [
      "Common kitchen uses include a spoonful in smoothies, oats, juice, herbal tea, yoghurt, or simple home recipes.",
      "Moss Matrix gel is prepared so it is easy to keep cold and use as a food ingredient in routines you already have.",
    ],
  },
  {
    id: "gold-purple",
    title: "Gold vs Purple",
    body: [
      "Gold is often chosen for a smooth, everyday look and feel. Purple has a deeper colour and is priced as our richer visual base.",
      "Both are fridge-ready sea moss gel. The choice is about colour, preference, and routine — not a medical strength ranking.",
    ],
  },
  {
    id: "jar-pouch",
    title: "Jar vs pouch",
    body: [
      "Jars feel premium and fridge-ready on the shelf. Pouches are lighter to ship and can lower breakage risk in transit — same fresh gel inside.",
    ],
  },
  {
    id: "mixins",
    title: "Mix-ins people commonly research",
    body: [
      "People often research ginger, turmeric, cinnamon, and other powdered botanicals as food pairings with sea moss.",
      "Moss Matrix keeps mix-ins educational on the website while plain Gold and Purple stay the simple order path.",
    ],
  },
  {
    id: "we-do-not-claim",
    title: "What we do not claim",
    body: [
      "We do not claim that sea moss boosts energy, supports immunity, detoxes the body, improves gut or thyroid health, aids recovery or sleep, supports brain health, causes weight loss, cures or heals conditions, or works as a natural multivitamin.",
      "Those topics appear in public search and conversation. We treat them as commonly researched subjects — not product promises.",
    ],
  },
];

export default function SeaMossBenefitsPage() {
  const path = "/learn/sea-moss-benefits";

  return (
    <SiteShell theme="dark">
      <SEO
        title="Sea Moss Benefits: What People Commonly Research | Moss Matrix"
        description="Educational guide to what people commonly research about sea moss. Moss Matrix Sea Moss Gel is sold as a food product, not a medicine, supplement, or performance product."
        path={path}
      />
      <ContentPageLayout
        eyebrow="Learn"
        title="Sea Moss Benefits: What People Commonly Research"
        subtitle="A calm guide to topics people look up — framed as education, not product claims."
      >
        <DarkCard className="mb-10 max-w-3xl border border-gold/25">
          <p className="text-sm text-sage leading-relaxed m-0">
            This guide is for education only. Moss Matrix Sea Moss Gel is sold as a food product, not a medicine,
            supplement, or performance product.
          </p>
        </DarkCard>

        <div className="space-y-5 max-w-3xl mb-12">
          {SECTIONS.map((section) => (
            <DarkCard key={section.id} as="section" aria-labelledby={section.id}>
              <h2 id={section.id} className="font-sans font-semibold text-xl text-ivory m-0">
                {section.title}
              </h2>
              <div className="mt-3 space-y-3">
                {section.body.map((p) => (
                  <p key={p.slice(0, 48)} className="text-sm text-sage leading-relaxed m-0">
                    {p}
                  </p>
                ))}
              </div>
            </DarkCard>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 mb-12">
          <Link to="/shop" className={`${HOME_CTA_DARK_PRIMARY} px-7 py-3.5 text-sm`}>
            Shop Moss Matrix
          </Link>
          <Link to="/mix-ins" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            Explore Mix-Ins
          </Link>
          <Link to="/learn/sea-moss-research" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            Research & citations
          </Link>
          <Link to="/learn/sea-moss-nutrition" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            Minerals & amino acids
          </Link>
          <Link to="/learn/gold-vs-purple-sea-moss" className={`${HOME_CTA_DARK_OUTLINE} px-7 py-3.5 text-sm`}>
            Gold vs Purple
          </Link>
        </div>

        <ConversionCtas />
      </ContentPageLayout>
    </SiteShell>
  );
}
